import React, { useState, useImperativeHandle, useEffect, forwardRef, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import { plural, trans } from '../../../../_providers/Translation'
import subscriptionHeadImage from "@/assets/img/contractors/icons/subscription-head.svg"

//images
import docIcon from '../../../../assets/img/icons/document.png'
import closeIcon from '../../../../assets/img/icons/close.png'
import loader from '../../../../assets/img/loader.svg'
import Success from '../../../modals/messages/Success'
import Error from '../../../modals/messages/Error'
import Dropdown from '../../../partials/Dropdown'
import { Checkbox, FormControlLabel, Switch, Tab, Tabs } from '@mui/material'
import SubscriptionDevice from './SubscriptionDevice'
import Skeleton from 'react-loading-skeleton'

function Subscription(props, ref) {

    const [popup, setPopup] = useState({
        overlay: false,
        modal: false,
        loading: false,
        data: null,
        id: null,
        edit: false,
        loadingData: false,
    })
    const [activeTab, setActiveTab] = useState(1)
    const [validations, setValidations] = useState(null)
    const [devices, setDevices] = useState([])

    const successModalRef = useRef(null)
    const errorModalRef = useRef(null)

    useImperativeHandle(ref, () => ({
        open: (id) => {
            handleShow(id)
        },
        hide: () => {
            handleHide()
        }
    }))

    useEffect(() => {
        if (popup.edit) {
            loadData()
        }
    }, [popup.edit])

    const loadData = () => {
        if (!popup.id) {
            return
        }

        setPopup(prev => ({
            ...prev,
            loadingData: true,
        }))

        Api.get(`contractors/subscriptions/find?id=${popup.id}`)
            .then(res => {
                setPopup(prev => ({
                    ...prev,
                    data: res.data,
                }))
                // Load devices from data if available
                if (res.data?.appliances && Array.isArray(res.data.appliances)) {
                    setDevices(res.data.appliances.map((device, idx) => ({
                        ...device,
                        _key: device.id || Date.now() + idx
                    })))
                }
            })
            .finally(() => {
                setPopup(prev => ({
                    ...prev,
                    loadingData: false,
                }))
            })
    }

    const handleShow = (id = null) => {
        setPopup(prev => ({
            ...prev,
            id: id,
            edit: id ? new Date() : null,
        }))

        show()
    }

    const handleHide = e => {
        e.preventDefault()

        setPopup(prev => ({
            ...prev,
            id: null,
            data: null,
            edit: false,
        }))
        setActiveTab(1)

        setValidations(null)
        setDevices([])

        hide().then(() => {
            if (typeof props.onClose === 'function') {
                props.onClose()
            }
        })

    }

    const show = async () => {
        return new Promise((resolve, reject) => {
            setPopup(prev => ({
                ...prev,
                overlay: true,
            }))

            setTimeout(() => {
                setPopup(prev => ({
                    ...prev,
                    overlay: true,
                    modal: true
                }))

                return resolve()
            }, 50)
        })
    }

    const hide = async () => {
        return new Promise((resolve, reject) => {
            setPopup(prev => ({
                ...prev,
                modal: false,
                loading: false,
            }))

            setTimeout(() => {
                setPopup(prev => ({
                    ...prev,
                    overlay: false,
                    modal: false,
                }))

                return resolve()
            }, 50)
        })
    }

    const handleSave = e => {
        e.preventDefault()

        setPopup(prev => ({
            ...prev,
            loading: true,
        }))

        setValidations(null)

        let url = 'contractors/subscriptions/' + (popup.id ? 'edit' : 'add')

        let data = new FormData(e.target)

        if (props.contractorId) {
            data.append('contractor_id', props.contractorId)
        }

        if (popup.id) {
            data.append('id', popup.id)
        }

        Api.post(url, data)
            .then(res => {

                handleHide(e)

                if (typeof props.onSave === 'function') {
                    props.onSave(res.data)
                }

            }).catch(error => {

                setPopup(prev => ({
                    ...prev,
                    loading: false,
                }))

                let _err = error.response

                if (_err && _err.status && _err.status == 422) {
                    // Tab 1 fields (primary)
                    const tab1Fields = ['type_id', 'name', 'contractor_id', 'is_active']
                    let hasTab1Error = false
                    let hasTab2Error = false

                    for (let [input, err] of Object.entries(_err.data.errors)) {
                        setValidations(prevState => {
                            return { ...prevState, [input]: true }
                        })

                        // Check if error is in tab 1
                        if (tab1Fields.includes(input)) {
                            hasTab1Error = true
                        }
                        // Check if error is in tab 2 (appliances fields)
                        else if (input.startsWith('appliances.')) {
                            hasTab2Error = true
                        }
                    }

                    // Focus tab with errors, prioritizing tab 1 (primary)
                    if (hasTab1Error) {
                        setActiveTab(1)
                    } else if (hasTab2Error) {
                        setActiveTab(2)
                    }
                }

            })
    }

    const handleTypeChange = data => {
        setPopup(prev => ({
            ...prev,
            data: {
                ...prev.data,
                type: data
            }
        }))
    }

    const handleContractorChange = data => {
        setPopup(prev => ({
            ...prev,
            data: {
                ...prev.data,
                contractor: data
            }
        }))
    }

    const handleInputChange = e => {
        let name = e.target.name
        let val = e.target.value

        setPopup(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: val
            }
        }))
    }

    const updateDeviceField = (index, field, value) => {
        setDevices(prev => {
            const next = prev.slice()
            next[index] = { ...(next[index] || {}), [field]: value }
            return next
        })
    }

    const handleAddDevice = () => {
        setDevices(prev => [...prev, { _key: Date.now() }])
    }

    const handleRemoveDevice = (index) => {
        setDevices(prev => {
            const next = prev.slice()
            next.splice(index, 1)
            return next
        })
    }

    const handleSwitchChange = e => {
        const { name, checked } = e.target

        setPopup(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: checked ? 1 : 0,
            }
        }))
    }

    return (
        <>
            <Success
                ref={successModalRef}
                mainMessage={trans('headings.success6')}
            />

            <Error
                ref={errorModalRef}
                mainMessage={trans('headings.error3')}
            />
            <div className={`${popup.overlay ? 'visible' : ''} overlay`}>
                <form className={`${popup.modal ? 'show' : ''} popup-primary popup-subscription-type medium`} onSubmit={handleSave}>
                    {popup.data?.contractor_id ? <input type="hidden" name="contractor_id" value={popup.data?.contractor_id} /> : <></>}

                    <div className='head-new' style={{ marginBottom: "10px" }}>
                        <img src={subscriptionHeadImage} alt="icon" />
                        <div className='texts'>
                            <h2>
                                {trans('headings.subscription')}
                            </h2>
                            <p>
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                            </p>
                        </div>
                        <span className='close' onClick={handleHide}>

                        </span>
                    </div>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        indicatorColor="primary"
                        textColor="primary"
                        aria-label="scrollable force tabs example"
                    >
                        <Tab value={1} label="Основни" />
                        <Tab value={2} label={`Уреди (${devices.length})`} />
                    </Tabs>
                    {popup.loadingData ?
                        <Skeleton height={45} count={5} />
                        :
                        <>
                            <div hidden={activeTab !== 1}>
                                <div className='row'>
                                    <div className='col'>
                                        <label>
                                            {trans('labels.name')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={popup.data?.name || ''}
                                            onChange={handleInputChange}
                                            className={validations && validations.name ? 'validate' : ''}
                                        />
                                    </div>
                                    <div className="col">
                                        <label>
                                            {trans('labels.type')}
                                        </label>
                                        <Dropdown
                                            inputName=""
                                            inputValue={popup.data?.type?.name || ''}
                                            inputIdName="type_id"
                                            inputIdValue={popup.data?.type?.id || ''}
                                            url="contractors/subscriptions/types"
                                            params={{
                                                id: popup.data?.type?.id || null,
                                                limit: 100
                                            }}
                                            inputClassName={validations && validations.type_id ? 'validate' : ''}
                                            onChange={handleTypeChange}
                                        />
                                    </div>
                                </div>
                                <hr />
                                <div className='row'>
                                    <div className='col'>
                                        <label>
                                            {trans('labels.company')}
                                        </label>
                                        <Dropdown
                                            inputName=""
                                            inputValue={popup.data?.contractor?.name || ''}
                                            inputIdName="contractor_id"
                                            inputIdValue={popup.data?.contractor?.id || ''}
                                            url="contractors/dropdown-list"
                                            params={{
                                                id: popup.data?.contractor?.id || null,
                                                limit: 100
                                            }}
                                            inputClassName={validations && validations.contractor_id ? 'validate' : ''}
                                            onChange={handleContractorChange}
                                            inputDisabled={props.contractorId}
                                        />
                                    </div>
                                </div>
                                <hr />
                                <div className='row'>
                                    <div className='col'>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="is_active"
                                                    checked={popup.data?.is_active ? true : false}
                                                    onChange={handleSwitchChange}
                                                    value={popup.data?.is_active}
                                                />
                                            }
                                            label={trans('labels.activeSubscription')}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div hidden={activeTab !== 2}>
                                {devices.map((device, index) => (
                                    <SubscriptionDevice
                                        key={device.id || device._key || index}
                                        device={device}
                                        index={index}
                                        onUpdateField={updateDeviceField}
                                        onRemove={handleRemoveDevice}
                                        validations={validations}
                                    />
                                ))}
                                <div className='row'>
                                    <button className="button-add button-small" type='button' onClick={handleAddDevice}>
                                        Добави устройство
                                    </button>
                                </div>
                            </div>
                        </>
                    }

                    <div className="footer align-right">
                        {popup.loading ?
                            <img src={loader} alt="loading" />
                            :
                            <>
                                <button className="button" onClick={handleHide}>
                                    {trans('buttons.cancel')}
                                </button>
                                <button className="button button-primary" type="submit">
                                    {trans('buttons.save')}
                                </button>
                            </>
                        }
                    </div>
                </form>
            </div>
        </>
    )
}

export default forwardRef(Subscription)